import { useEffect, useState } from 'react'
import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
  isEmptyObject,
  isPresent,
  isValidUrl,
  separateUrlProtocol,
} from 'utils'
import { Modal } from 'components/dialog/Modals'
import { PreviewConfirmModal } from 'components/dialog/ModalTemplate'
import useValidator from 'utils/hooks/useValidator'
import {
  editKeywordLandingUrl,
  hideKeywordLandingUrlEdit,
  hidePreview,
  setIsIndividual,
  showPreview,
} from 'features/dashboard/operation/keywordLandingUrlEdit/keywordLandingUrlEditModalSlice'
import { refreshDashboard } from 'features/dashboard/dashboardCommonSlice'
import { failWithErrorResponse, success } from 'features/toast/Toast'
import CheckBox from 'components/form/CheckBox'
import TextInput from 'components/form/TextInput'
import ErrorText from 'components/form/ErrorText'
import Table from 'components/table/Table'
import classNames from 'classnames'
import { getSiteText } from 'features/shared/SiteUrlLink'
import { setSelectedIds } from 'features/dashboard/table/keyword/keywordTableSlice'
import FixedTable from 'components/table/FixedTable'
import HelpTooltip from 'components/tooltip/HelpTooltip'

const KeywordLandingUrlEditModal = () => {
  const dispatch = useDispatch()
  const {
    visible,
    previewVisible,
    siteSetting,
    isSubmitting,
    keywords,
    form: { isIndividual, unifiedURL, pcURL, mobileURL },
    preview: {
      isIndividual: previewIsIndividual,
      unifiedURL: previewUnifiedURL,
      pcURL: previewPcURL,
      mobileURL: previewMobileURL,
    },
  } = useSelector(
    state => state.dashboard.operation.keywordLandingUrlEditModal,
    shallowEqual
  )
  const [unifiedURLInput, setUnifiedURLInput] = useState(unifiedURL)
  const [pcURLInput, setPcURLInput] = useState(pcURL)
  const [mobileURLInput, setMobileURLInput] = useState(mobileURL)
  const { errors, validate, clearError } = useValidator(() => {
    const needPcOnly = siteSetting.isIndividual && !siteSetting.hasMobileSite
    const needMobileOnly = siteSetting.isIndividual && !siteSetting.hasPcSite
    const needBoth =
      (siteSetting.isIndividual &&
        siteSetting.hasMobileSite &&
        siteSetting.hasPcSite) ||
      (siteSetting.isIndividual === false && isIndividual === true)
    return {
      ...(isIndividual
        ? {
            pcError:
              !needMobileOnly &&
              ((needPcOnly || needBoth
                ? !isPresent(pcURLInput) && 'PC URL을 입력해주세요.'
                : !isPresent(pcURLInput) &&
                  !isPresent(mobileURLInput) &&
                  '모바일/PC URL중 적어도 하나는 입력해야 합니다.') ||
                (isPresent(pcURLInput) &&
                  !isValidUrl(pcURLInput) &&
                  '올바른 URL 주소가 아닙니다.')),
            mobileError:
              !needPcOnly &&
              ((needMobileOnly || needBoth
                ? !isPresent(mobileURLInput) && '모바일 URL을 입력해주세요.'
                : !isPresent(pcURLInput) &&
                  !isPresent(mobileURLInput) &&
                  '모바일/PC URL중 적어도 하나는 입력해야 합니다.') ||
                (isPresent(mobileURLInput) &&
                  !isValidUrl(mobileURLInput) &&
                  '올바른 URL 주소가 아닙니다.')),
          }
        : {
            unifiedError:
              (!isPresent(unifiedURLInput) && '웹사이트 URL을 입력해주세요.') ||
              (!isValidUrl(unifiedURLInput) && '올바른 URL 주소가 아닙니다.'),
          }),
    }
  }, [
    pcURLInput,
    mobileURLInput,
    unifiedURLInput,
    isIndividual,
    siteSetting.hasPcSite,
    siteSetting.hasMobileSite,
  ])

  useEffect(() => {
    if (visible) {
      clearError()
      setUnifiedURLInput(unifiedURL)
      setPcURLInput(pcURL)
      setMobileURLInput(mobileURL)
    }
  }, [visible, unifiedURL, pcURL, mobileURL, clearError])

  const handlePreview = async () => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        dispatch(
          showPreview({
            unifiedURL: unifiedURLInput,
            pcURL: pcURLInput,
            mobileURL: mobileURLInput,
          })
        )
      }
    } catch (ignore) {}
  }
  const handleConfirm = async ({ preventClose }) => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        const commands = keywords.map(({ id: keywordId }) => ({
          keywordId,
          landingUrl: {
            ...(isIndividual
              ? {
                  individualSettingYn: 'Y',
                  pcLandingUrl: pcURLInput
                    ? (([protocol, url]) => ({
                        url,
                        protocol: protocol.toUpperCase(),
                      }))(separateUrlProtocol(pcURLInput))
                    : null,
                  mobileLandingUrl: mobileURLInput
                    ? (([protocol, url]) => ({
                        url,
                        protocol: protocol.toUpperCase(),
                      }))(separateUrlProtocol(mobileURLInput))
                    : null,
                }
              : {
                  individualSettingYn: 'N',
                  pcLandingUrl: unifiedURLInput
                    ? (([protocol, url]) => ({
                        url,
                        protocol: protocol.toUpperCase(),
                      }))(separateUrlProtocol(unifiedURLInput))
                    : null,
                }),
          },
        }))
        const { error, payload } = await dispatch(
          editKeywordLandingUrl(commands)
        )
        if (error) {
          preventClose()
          failWithErrorResponse(payload, {
            messageMap: {
              105000: '이미 삭제된 키워드는 수정할 수 없습니다.',
            },
          })
        } else {
          success('키워드 변경사항이 적용되었습니다.')
          batch(() => {
            dispatch(setSelectedIds([]))
            dispatch(refreshDashboard())
          })
        }
      }
    } catch (error) {
      preventClose()
    }
  }

  useEffect(() => {
    if (!isEmptyObject(errors)) {
      dispatch(hidePreview())
    }
  }, [dispatch, errors])
  useEffect(
    () => () => {
      dispatch(hideKeywordLandingUrlEdit())
    },
    [dispatch]
  )

  const preview = previewVisible && isEmptyObject(errors)
  const changed =
    isIndividual !== previewIsIndividual ||
    unifiedURLInput !== previewUnifiedURL ||
    pcURLInput !== previewPcURL ||
    mobileURLInput !== previewMobileURL

  return (
    <Modal on={visible} className="landing_layer1">
      <PreviewConfirmModal
        title="키워드 랜딩 URL 변경"
        isSubmitting={isSubmitting}
        onPreview={handlePreview}
        onConfirm={handleConfirm}
        previewDisabled={!changed && preview}
        confirmDisabled={changed && preview}
        handleClose={() => dispatch(hideKeywordLandingUrlEdit())}>
        <p className="desc_info">
          키워드의 랜딩 URL을 설정하신 경우 광고 클릭시 소재의 URL이 아닌
          키워드에 설정된 URL로 연결됩니다.
        </p>
        <div className="box_subcard">
          <strong className="tit_subcard">
            {isIndividual ? '모바일' : '모바일/PC'}
          </strong>
          <HelpTooltip style={{ float: 'right' }} position="top alignRight">
            비즈채널 URL 개별 설정에 따라 활성화 여부가 결정됩니다.
          </HelpTooltip>
          <CheckBox
            label="개별 설정"
            disabled={siteSetting.isIndividual}
            checked={isIndividual}
            onCheckedChange={v => dispatch(setIsIndividual(v))}
          />
          {isIndividual ? (
            <>
              <div className="item_landing">
                <div className="f_left">
                  <div className="single_wrap">
                    <TextInput
                      value={mobileURLInput}
                      onValueChange={v => setMobileURLInput(v)}
                      error={errors.mobileError}
                      disabled={
                        siteSetting.isIndividual && !siteSetting.hasMobileSite
                      }
                      placeholder="http:// 또는 https:// 형식의 정상적인 랜딩 URL을 입력하세요."
                    />
                    <ErrorText error={errors.mobileError} />
                  </div>
                </div>
              </div>
              <strong className="tit_subcard">PC</strong>
              <div className="item_landing">
                <div className="f_left">
                  <div className="single_wrap">
                    <TextInput
                      value={pcURLInput}
                      onValueChange={v => setPcURLInput(v)}
                      error={errors.pcError}
                      disabled={
                        siteSetting.isIndividual && !siteSetting.hasPcSite
                      }
                      placeholder="http:// 또는 https:// 형식의 정상적인 랜딩 URL을 입력하세요."
                    />
                    <ErrorText error={errors.pcError} />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="item_landing">
              <div className="f_left">
                <div className="single_wrap">
                  <TextInput
                    value={unifiedURLInput}
                    onValueChange={v => setUnifiedURLInput(v)}
                    error={errors.unifiedError}
                    placeholder="http:// 또는 https:// 형식의 정상적인 랜딩 URL을 입력하세요."
                  />
                  <ErrorText error={errors.unifiedError} />
                </div>
              </div>
            </div>
          )}
        </div>
        {preview && (
          <FixedTable caption="키워드 랜딩 URL 변경 이력 표">
            <Table.Head>
              <Table.Column
                title="선택키워드"
                className="inner_cell"
                width={159}
              />
              <Table.Column
                title="변경 전"
                className="inner_cell"
                width={189}
              />
              <Table.Column title="변경 후" className="inner_cell" />
            </Table.Head>
            <Table.Body>
              {keywords.map(keyword => {
                const { id, text, landingUrl } = keyword
                const before = landingUrl
                  ? {
                      isIndividual: landingUrl.individualSettingYn === 'Y',
                      unifiedURL:
                        landingUrl.individualSettingYn !== 'Y'
                          ? getSiteText(
                              landingUrl.pcLandingUrl.protocol,
                              landingUrl.pcLandingUrl.url
                            )
                          : undefined,
                      pcURL:
                        landingUrl.individualSettingYn === 'Y' &&
                        landingUrl.pcLandingUrl
                          ? getSiteText(
                              landingUrl.pcLandingUrl.protocol,
                              landingUrl.pcLandingUrl.url
                            )
                          : undefined,
                      mobileURL:
                        landingUrl.individualSettingYn === 'Y' &&
                        landingUrl.mobileLandingUrl
                          ? getSiteText(
                              landingUrl.mobileLandingUrl.protocol,
                              landingUrl.mobileLandingUrl.url
                            )
                          : undefined,
                    }
                  : undefined
                const isUpdated =
                  before === undefined ||
                  before.isIndividual !== previewIsIndividual ||
                  (!before.isIndividual &&
                    before.unifiedURL !== previewUnifiedURL) ||
                  (before.isIndividual && before.pcURL === previewPcURL) ||
                  (before.isIndividual && before.mobileURL === previewMobileURL)
                return (
                  <Table.Row
                    rowId={id}
                    key={id}
                    className={classNames({ update: isUpdated })}>
                    <td>
                      <div className="inner_cell">
                        <span className="txt_limit3">{text}</span>
                      </div>
                    </td>
                    <td>
                      <div className="inner_cell">
                        {before ? (
                          <>
                            {before.isIndividual ? (
                              <>
                                {before.mobileURL && (
                                  <div className="box_url url_view">
                                    <strong className="tit_url">
                                      [모바일]
                                    </strong>
                                    <div className="txt_url">
                                      <a
                                        href={before.mobileURL}
                                        className="link_txt"
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        {before.mobileURL}
                                      </a>
                                    </div>
                                  </div>
                                )}
                                {before.pcURL && (
                                  <div className="box_url url_view">
                                    <strong className="tit_url">[PC]</strong>
                                    <div className="txt_url">
                                      <a
                                        href={before.pcURL}
                                        className="link_txt"
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        {before.pcURL}
                                      </a>
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="box_url url_view">
                                <strong className="tit_url">[모바일/PC]</strong>
                                <div className="txt_url">
                                  <a
                                    href={before.unifiedURL}
                                    className="link_txt"
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    {before.unifiedURL}
                                  </a>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          '미설정'
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="inner_cell">
                        {previewIsIndividual ? (
                          <>
                            {previewMobileURL && (
                              <div className="box_url url_view">
                                <strong className="tit_url">[모바일]</strong>
                                <div className="txt_url">
                                  <a
                                    href={previewMobileURL}
                                    className="link_txt"
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    {previewMobileURL}
                                  </a>
                                </div>
                              </div>
                            )}
                            {previewPcURL && (
                              <div className="box_url url_view">
                                <strong className="tit_url">[PC]</strong>
                                <div className="txt_url">
                                  <a
                                    href={previewPcURL}
                                    className="link_txt"
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    {previewPcURL}
                                  </a>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="box_url url_view">
                            <strong className="tit_url">[모바일/PC]</strong>
                            <div className="txt_url">
                              <a
                                href={previewUnifiedURL}
                                className="link_txt"
                                target="_blank"
                                rel="noopener noreferrer">
                                {previewUnifiedURL}
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </Table.Row>
                )
              })}
            </Table.Body>
          </FixedTable>
        )}
      </PreviewConfirmModal>
    </Modal>
  )
}
export default KeywordLandingUrlEditModal
